import PropTypes from 'prop-types';
import React from 'react';
import { Route, Switch } from 'react-router-dom';
import classNames from 'classnames/bind';
import get from 'lodash/get';
import noop from 'lodash/noop';
import isEmpty from 'lodash/isEmpty';
import once from 'lodash/once';
import getBrowserHistory from 'yoda-core-components/lib/navigation/history/getBrowserHistory';
import Loader from 'yoda-core-components/lib/components/Loader/Loader';
import SiteComponent from 'yoda-site-components/lib/components/SiteComponent/SiteComponent';
import CertonaZone from 'yoda-site-components/lib/components/CertonaZone/CertonaZone';
import CAMErrorComponent from 'yoda-account-components/lib/components/CAMErrorComponent/CAMErrorComponent';
import CAMNavLayout from 'yoda-site-components/lib/components/CAMNav/WithLayout';
import TokenProvider from 'yoda-core-components/lib/helpers/TokenProvider/TokenProvider';
import NotFound from 'yoda-app-shell/lib/components/NotFound/NotFound';
import DashboardWrapper from './Components/HomeWrapperLazy';
import AppointmentWrapper from './Components/AppointmentWrapper';
import WalletWrapper from './Components/WalletWrapperLazy';
import MyInfoWrapperLazy from './Components/MyInfoWrapperLazy';
import NotificationPreferencesWrapper from './Components/NotificationPreferencesWrapper';

import * as styles from './CAMDashboard.css';

const cx = classNames.bind(styles);

class CAMDashboard extends SiteComponent {
    static propTypes = {
        accountDetailsActions: PropTypes.objectOf(PropTypes.func).isRequired,
        location: PropTypes.shape({
            pathname: PropTypes.string.isRequired,
            basename: PropTypes.string,
            search: PropTypes.string,
        }).isRequired,
        sliderActions: PropTypes.objectOf(PropTypes.func).isRequired,
        redirectAction: PropTypes.objectOf(PropTypes.func).isRequired,
        actions: PropTypes.objectOf(PropTypes.func).isRequired,
        accountDetails: PropTypes.shape({
            isSuccess: PropTypes.bool,
            errorProfile: PropTypes.bool,
            isLoading: PropTypes.bool,
        }),
        userProfile: PropTypes.object.isRequired,
        onVerifyOtpSuccess: PropTypes.bool.isRequired,
        deviceType: PropTypes.shape({
            isDesktop: PropTypes.bool,
            isMobile: PropTypes.bool,
        }),
        featureFlags: PropTypes.shape({
            disableRecZone: PropTypes.bool,
        }),
        preferences: PropTypes.shape({
            serviceDownError: PropTypes.object,
            allowedRouteForGuest: PropTypes.array,
            googleBrowserKey: PropTypes.string,
        }),
        headers: PropTypes.shape({
            cookies: PropTypes.object,
        }),
        router: PropTypes.shape({
            location: PropTypes.object,
            isActive: PropTypes.func,
        }),
        syfQuickScreen: PropTypes.object.isRequired,
        analyticsActions: PropTypes.objectOf(PropTypes.func).isRequired,
    };

    static defaultProps = {
        accountDetails: {},
        userProfile: {},
        router: {
            isActive: noop,
            location: {},
        },
        location: {
            basename: '',
            search: '',
        },
    };

    static contextTypes = {
        search: PropTypes.objectOf(PropTypes.object),
        hostname: PropTypes.string,
        isBrowser: PropTypes.bool,
    };

    constructor(props) {
        super();
        const deviceType = props.deviceType.isDesktop ? 'desktop' : 'mobile';
        this.state = {
            deviceType,
        };

        this.getRewardsCerts = once(() => TokenProvider.get('DP_REWARDS_CERTS'));
        this.getRewardsPointsAway = once(() => TokenProvider.get('DP_REWARDS_POINTS_AWAY'));
    }

    componentWillReceiveProps(nextProps) {
        if (
            this.props.userProfile.userState &&
            nextProps.userProfile.userState &&
            nextProps.userProfile.userState !== this.props.userProfile.userState
        ) {
            this.reloadReduxForUser(nextProps.userProfile.userState);
        } else if (
            nextProps.onVerifyOtpSuccess &&
            nextProps.onVerifyOtpSuccess !== this.props.onVerifyOtpSuccess &&
            this.props.location.pathname !== '/account/dashboard/personal/info'
        ) {
            this.props.accountDetailsActions.getLightProfile();
        }
    }

    hydrateOnMount() {
        if (!this.props.accountDetails.isSuccess || this.props.accountDetails.errorProfile) {
            this.reloadReduxForUser();
        }
    }

    getLocationString = (pathname = '') =>
        `${pathname.endsWith('/') ? pathname.slice(0, -1) : pathname}`;

    reloadReduxForUser = (
        userState = get(
            this.props,
            'headers.cookies.DP_USER_STATE',
            TokenProvider.get('DP_USER_STATE')
        )
    ) => {
        if (userState !== '0' && userState !== undefined) {
            this.props.accountDetailsActions.getProfile(this.props.headers);
        } else if (!this.isAllowedRouteForGuest()) {
            const redirectUrl = '/account/dashboard?showNavPanel=true';
            if (!__SERVER__) {
                getBrowserHistory().push(redirectUrl);
            } else {
                // this condition will execute only in hydrate at SSR
                const { location, redirectAction } = this.props;
                !['/account/dashboard?showNavPanel=true', '/dashboard?showNavPanel=true'].includes(
                    `${this.getLocationString(location.pathname)}${location.search}`
                ) && redirectAction.setRedirectUrl({ url: redirectUrl });
            }
        }
    };

    isAllowedRouteForGuest = () =>
        get(this.props, 'preferences.allowedRouteForGuest', [
            '/account/dashboard/appointments',
        ]).includes(this.props.location.pathname);

    isErrorComponentNeeded = () =>
        get(this.props, 'preferences.serviceDownError.includedRouteList', []).includes(
            this.getLocationString(this.props.match.url)
        );

    checkValidErrorComp = (error = {}) =>
        !isEmpty(error) &&
        !get(this.props, 'preferences.serviceDownError.exceptionalErrors', []).includes(
            error.errorCode
        );

    getAccountDashboardBg = () => {
        const {
            syfQuickScreen: {
                offerDetails: { cardType = '' } = {},
                isSuccess: isQSAPIResponded,
            } = {},
            featureFlags: { enableQuickScreenBanner = true, enableCrossBrand = false },
        } = this.props;
        let backGroundClass = !enableCrossBrand ? 'default-bg' : 'cross-brand-bg';
        if (!enableQuickScreenBanner) return backGroundClass;
        if (isQSAPIResponded) {
            if (cardType === 'DUAL') {
                backGroundClass = !enableCrossBrand ? 'mastercard-bg' : 'cross-brand-card-bg';
            } else if (cardType === 'JCP') {
                backGroundClass = !enableCrossBrand ? 'creditcard-bg' : 'cross-brand-card-bg';
            }
            return backGroundClass;
        }
        return backGroundClass;
    };

    render() {
        const {
            sliderActions,
            actions,
            analyticsAction,
            userProfile,
            accountDetails: { isLoading, errorProfile },
            preferences,
            accountDetailsActions,
            deviceType,
            featureFlags,
            match,
        } = this.props;
        const isDashboard = ['/account/dashboard', '/dashboard'].includes(
            this.getLocationString(match.url)
        );
        const rewardsCertonaAttributes = {};
        let recZoneSlotType = 'rewards2_rr';
        const DP_REWARDS_CERTS = this.getRewardsCerts();
        const DP_REWARDS_POINTS_AWAY = this.getRewardsPointsAway();

        if (DP_REWARDS_CERTS && DP_REWARDS_CERTS !== '0') {
            rewardsCertonaAttributes.availablerewards = DP_REWARDS_CERTS;
            recZoneSlotType = 'rewards1_rr';
        } else if (DP_REWARDS_POINTS_AWAY && DP_REWARDS_POINTS_AWAY !== '0') {
            rewardsCertonaAttributes.dollartonextrewards = DP_REWARDS_POINTS_AWAY;
        } else {
            /* istanbul ignore next */
            rewardsCertonaAttributes.dollartonextrewards = '30.00';
        }

        const renderRecZone = () => {
            if (!featureFlags.disableRecZone) {
                return (
                    <CertonaZone
                        page="ACCOUNT"
                        slotDetails={{
                            pageType: 'REWARDS',
                            attributes: {
                                schemes: recZoneSlotType,
                                recommendations: true,
                                ...rewardsCertonaAttributes,
                            },
                            forceRefresh: true,
                            loader: 'certona',
                            slotId: recZoneSlotType,
                        }}
                        schemasToRender={[recZoneSlotType]}
                    />
                );
            }
            /* istanbul ignore next */
            return undefined;
        };
        const pageTitle =
            get(userProfile, 'userState', '0') !== '0' && userProfile.firstName
                ? `Hi, ${userProfile.firstName}!`
                : 'My Dashboard';
        return (
            <CAMNavLayout
                layoutState={{ title: pageTitle }}
                classes={{
                    panelContentContainer: cx('content-container'),
                    root: cx('layout-container', this.getAccountDashboardBg()),
                    layoutArticleClass: cx({
                        dashboardBg: isDashboard && !deviceType.isMobile,
                        desktop: this.props.deviceType.isDesktop,
                    }),
                }}
                actions={sliderActions}
                analyticsActions={actions}
                renderRecZone={renderRecZone}
                analyticsAction={analyticsAction}
            >
                {isLoading && <Loader keepOverlay automationId="test-automation-loader-1" />}
                {this.checkValidErrorComp(errorProfile) && this.isErrorComponentNeeded() ? (
                    <CAMErrorComponent
                        classes={{ wrapper: cx('camErrorComponent') }}
                        errorDetails={get(preferences, 'serviceDownError')}
                        hideRetryButton={!!get(preferences, 'serviceDownError.hideRetryButton')}
                        onRetry={() => {
                            accountDetailsActions.getProfile();
                        }}
                        buttonTitle={get(preferences, 'serviceDownError.buttonTitle')}
                    />
                ) : (
                    <Switch>
                        <Route
                            exact
                            path="/account/dashboard/stores"
                            component={DashboardWrapper}
                        />
                        <Route exact path="/account/dashboard/wallet" component={WalletWrapper} />
                        <Route
                            exact
                            path="/account/dashboard/personal/info"
                            component={MyInfoWrapperLazy}
                        />
                        <Route
                            exact
                            path="/account/dashboard/preferences"
                            component={NotificationPreferencesWrapper}
                        />
                        <Route
                            exact
                            path="/account/dashboard/orders"
                            component={DashboardWrapper}
                        />
                        <Route
                            exact
                            path="/account/dashboard/appointments"
                            component={AppointmentWrapper}
                        />
                        <Route exact path="/account/dashboard" component={DashboardWrapper} />

                        <Route component={NotFound} />
                    </Switch>
                )}
            </CAMNavLayout>
        );
    }
}

export default CAMDashboard;
